/* ===========================================================================
 * Discount Sort — "Maior desconto"
 * O sort_by do Shopify é enum fechado (sem desconto). Este módulo ordena no
 * cliente sobre TODO o conjunto filtrado (endpoint ?view=discount). A saída
 * respeita a paginação escolhida no editor: rolagem infinita (quando o
 * #ProductGridContainer tem a classe `infinity-scroll-active`) ou paginação
 * numerada própria. Ao trocar de sort, devolve o controle ao tema.
 * =========================================================================== */

(function () {
  'use strict';

  const SORT_VALUE = 'discount-descending';

  class DiscountSort {
    constructor() {
      this.container = null;
      this.observer = null;
      this.items = null; // [{ d, html }] ordenado
      this.cacheKey = null;
      this.owning = false;
      // Enquanto true, o usuário escolheu outra ordenação: não reassumir.
      this.blocked = false;

      // Rolagem infinita: nº de páginas já renderizadas + observer e botão.
      this.infinitePage = 1;
      this.infiniteObserver = null;
      this.loadMoreEl = null;
      this.loadMoreWrap = null;
      this.loadMoreLabel = null;

      this.onGridMutation = this.onGridMutation.bind(this);
      this.onContainerClick = this.onContainerClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onSortInput = this.onSortInput.bind(this);
    }

    init() {
      if (!this.globalsAttached) {
        window.addEventListener('popstate', this.onPopState);
        // Delegação no document: o <select name="sort_by"> vive num bloco
        // .js-filter que o facets RECRIA (renderFilters) ao aplicar filtros —
        // um listener preso ao select morreria. A delegação pega o input de
        // qualquer sort select atual. Capture p/ rodar antes de tudo.
        document.addEventListener('input', this.onSortInput, true);
        this.globalsAttached = true;
      }

      // (Re)liga ao #ProductGridContainer atual (o shopify:section:load o troca).
      const container = document.getElementById('ProductGridContainer');
      if (!container) return;

      if (this.container && this.container !== container) {
        this.container.removeEventListener('click', this.onContainerClick);
      }
      if (this.observer) this.observer.disconnect();

      this.container = container;
      this.observer = new MutationObserver(this.onGridMutation);
      this.observer.observe(this.container, { childList: true });
      this.container.addEventListener('click', this.onContainerClick);

      if (this.isActive() && !this.blocked) {
        this.reflectSelect();
        this.takeOver();
      }
    }

    // Troca de ordenação pelo usuário (sinal de intenção independente do timing
    // da URL / do reflectSelect). Ao escolher algo != "maior desconto", solta o
    // controle na hora e BLOQUEIA reassumir — senão o facets renderia a nova
    // ordem, mas um onGridMutation lendo a URL ainda defasada reassumiria o
    // discount (o bug do "volta para maior desconto"). Lê a escolha do
    // custom-select (imune à dirty flag do <select>).
    onSortInput(event) {
      const select = event.target;
      if (!select || select.name !== 'sort_by') return;

      const customSelect = select.closest('custom-select');
      const chosen =
        (customSelect && customSelect.dataset.value) || select.value;

      if (chosen === SORT_VALUE) {
        this.blocked = false;
      } else {
        this.blocked = true;
        if (this.owning) this.release();
      }
    }

    isActive() {
      return new URLSearchParams(window.location.search).get('sort_by') === SORT_VALUE;
    }

    /** Rolagem infinita ligada? O sinal é a mesma classe do grid nativo. */
    isInfinite() {
      return !!this.container && this.container.classList.contains('infinity-scroll-active');
    }

    currentPage() {
      const n = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
      return n > 0 ? n : 1;
    }

    perPage() {
      const grid = document.getElementById('product-grid');
      const n = grid ? parseInt(grid.dataset.perPage || '', 10) : NaN;
      return n > 0 ? n : 24;
    }

    /** Chave do conjunto: filtros + q, ignorando página/sort/section. */
    filterKey() {
      const p = new URLSearchParams(window.location.search);
      p.delete('page');
      p.delete('sort_by');
      p.delete('section_id');
      p.sort();
      return p.toString();
    }

    // Reage às renderizações AJAX do facets.js (o observer é desconectado
    // durante as nossas próprias escritas; `writing` cobre corridas residuais).
    onGridMutation() {
      if (this.writing) return;
      if (this.isActive() && !this.blocked) {
        this.reflectSelect();
        this.takeOver();
      } else if (this.owning) {
        this.release();
      }
    }

    onPopState() {
      // ⚠️ NÃO resetar `blocked` aqui. No MOBILE, o custom-select.js empilha uma
      // entrada-sentinela ao abrir o dropdown e faz history.back() ao escolher —
      // isso dispara um popstate cuja URL ainda é a de discount (o render do
      // facets só vem 500ms depois, no debounce). Resetar `blocked` aqui fazia o
      // discount reassumir logo após o usuário trocar de ordenação (só no
      // mobile). O `blocked` só volta a false quando o usuário ESCOLHE "maior
      // desconto" (onSortInput). O takeover segue respeitando `!this.blocked`.
      if (this.isActive() && !this.blocked) {
        this.reflectSelect();
        this.takeOver();
      } else if (this.owning) {
        this.release();
      }
    }

    async takeOver() {
      const key = this.filterKey();
      this.owning = true;

      try {
        if (key !== this.cacheKey || !this.items) {
          this.setLoading(true);
          const items = await this.fetchAll();
          items.sort((a, b) => b.d - a.d); // maior desconto primeiro; d=0 por último
          this.items = items;
          this.cacheKey = key;
          this.setLoading(false);
        }
        this.renderCurrent();
      } catch (error) {
        this.setLoading(false);
        this.owning = false;
        // Falha de rede: mantém o grid nativo e permite nova tentativa.
        console.error('[discount-sort] Falha ao ordenar por desconto. Mantendo a ordem padrão.', error);
      }
    }

    async fetchAll() {
      const base = new URLSearchParams(window.location.search);
      base.delete('page');
      base.delete('sort_by');
      base.delete('section_id');
      base.set('view', 'discount');

      const path = window.location.pathname;
      const first = await this.fetchPage(path, base, 1);
      let all = (first.products || []).slice();

      const rest = [];
      for (let p = 2; p <= (first.pages || 1); p += 1) {
        rest.push(this.fetchPage(path, base, p));
      }
      (await Promise.all(rest)).forEach(r => {
        all = all.concat(r.products || []);
      });
      return all;
    }

    async fetchPage(path, baseParams, page) {
      const params = new URLSearchParams(baseParams);
      params.set('page', String(page));
      const response = await fetch(`${path}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`view=discount page ${page} -> HTTP ${response.status}`);
      }
      return JSON.parse(await response.text());
    }

    /** Renderiza segundo o modo escolhido no editor. */
    renderCurrent() {
      if (this.isInfinite()) {
        this.renderInfinite(this.currentPage());
      } else {
        this.renderClientPage(this.currentPage());
      }
    }

    renderClientPage(page) {
      const grid = document.getElementById('product-grid');
      if (!grid || !this.items) return;

      const perPage = this.perPage();
      const total = this.items.length;
      const totalPages = Math.max(1, Math.ceil(total / perPage));
      page = Math.min(Math.max(1, page), totalPages);
      const slice = this.items.slice((page - 1) * perPage, (page - 1) * perPage + perPage);

      this.write(() => {
        this.teardownLoadMore(); // caso venha do modo infinito
        this.removeNativePagination();
        grid.innerHTML = slice.map(item => item.html).join('');
        this.renderPagination(grid, page, totalPages);
        this.updateCount(total);
      });
    }

    // --- Rolagem infinita -------------------------------------------------
    // Renderiza cumulativamente as páginas 1..uptoPage (suporta deep-link em
    // ?page=N) e planta um botão "Carregar mais" REAL após o grid; um
    // IntersectionObserver carrega a próxima fatia quando o botão entra no
    // viewport (o botão nativo <collection-load-more> foi removido pelo
    // takeover, por isso a rolagem é gerida aqui).
    renderInfinite(uptoPage) {
      const grid = document.getElementById('product-grid');
      if (!grid || !this.items) return;

      // Reaproveita o rótulo do botão nativo (i18n) antes de removê-lo.
      const nativeBtn = this.container.querySelector('.js-btn-load-more');
      if (nativeBtn && nativeBtn.textContent.trim()) {
        this.loadMoreLabel = nativeBtn.textContent.trim();
      }

      const perPage = this.perPage();
      const total = this.items.length;
      const totalPages = Math.max(1, Math.ceil(total / perPage));
      uptoPage = Math.min(Math.max(1, uptoPage), totalPages);
      this.infinitePage = uptoPage;
      const slice = this.items.slice(0, uptoPage * perPage);

      this.write(() => {
        this.removeNativePagination();
        this.removeDiscountPagination(); // caso venha do modo numerado
        grid.innerHTML = slice.map(item => item.html).join('');
        this.updateCount(total);
        this.mountLoadMore(grid, uptoPage < totalPages);
      });
    }

    loadNextInfinite() {
      const grid = document.getElementById('product-grid');
      if (!grid || !this.items) return;

      const perPage = this.perPage();
      const totalPages = Math.max(1, Math.ceil(this.items.length / perPage));
      if (this.infinitePage >= totalPages) {
        this.teardownLoadMore();
        return;
      }

      const next = this.infinitePage + 1;
      const slice = this.items.slice((next - 1) * perPage, next * perPage);
      this.infinitePage = next;

      this.write(() => {
        grid.insertAdjacentHTML('beforeend', slice.map(item => item.html).join(''));
        if (next >= totalPages) {
          this.teardownLoadMore();
        } else if (this.infiniteObserver && this.loadMoreEl) {
          // Reavalia a interseção: se o botão seguir visível (poucos itens por
          // página / viewport alto), continua carregando; senão espera a
          // próxima rolagem. Sem isto, o IO não re-dispara em conjuntos curtos.
          this.infiniteObserver.unobserve(this.loadMoreEl);
          this.infiniteObserver.observe(this.loadMoreEl);
        }
      });

      // Espelha o load-more nativo: atualiza ?page=N sem recarregar, para que
      // deep-link/refresh e o botão voltar preservem a profundidade rolada.
      history.replaceState({ discountPage: next }, '', this.pageUrl(next));
    }

    // Botão "Carregar mais" REAL (visível, clicável) após o grid. Um botão
    // visível é alvo confiável do IntersectionObserver (o sentinela invisível
    // de antes não disparava com fiabilidade, sobretudo no editor) e dá o mesmo
    // comportamento/UX do modo nativo, com fallback ao clique.
    mountLoadMore(grid, hasMore) {
      this.teardownLoadMore();
      if (!hasMore) return;

      const wrap = document.createElement('div');
      wrap.className = 'collection__actions text-center js-discount-loadmore';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button button--outlined js-discount-load-more';
      btn.textContent = this.loadMoreLabel || 'Carregar mais';
      btn.addEventListener('click', () => this.loadNextInfinite());

      wrap.appendChild(btn);
      grid.insertAdjacentElement('afterend', wrap);
      this.loadMoreWrap = wrap;
      this.loadMoreEl = btn;

      this.infiniteObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) this.loadNextInfinite();
          });
        },
        // Pré-carrega antes de o botão chegar ao viewport (rolagem suave).
        { rootMargin: '400px 0px' }
      );
      this.infiniteObserver.observe(btn);
    }

    teardownLoadMore() {
      if (this.infiniteObserver) {
        this.infiniteObserver.disconnect();
        this.infiniteObserver = null;
      }
      if (this.container) {
        const wrap = this.container.querySelector('.js-discount-loadmore');
        if (wrap) wrap.remove();
      }
      this.loadMoreWrap = null;
      this.loadMoreEl = null;
    }

    removeDiscountPagination() {
      const nav = this.container.querySelector('.js-discount-pagination');
      if (nav) nav.remove();
    }

    // Escreve no DOM sem que o nosso observer reprocesse a própria mudança:
    // desconecta, escreve, e reconecta no frame seguinte.
    write(fn) {
      this.writing = true;
      if (this.observer) this.observer.disconnect();
      try {
        fn();
      } finally {
        requestAnimationFrame(() => {
          if (this.observer && this.container) {
            this.observer.observe(this.container, { childList: true });
          }
          this.writing = false;
        });
      }
    }

    // Com o sort de desconto ativo, o facets renderiza a paginação nativa do
    // tema junto do grid; removemo-la para não ter paginação dupla (a nativa
    // recarrega a página inteira). O facets a recria ao sair deste sort.
    removeNativePagination() {
      this.container
        .querySelectorAll('.collection__actions, collection-load-more, collection-load-previous')
        .forEach(el => el.remove());
    }

    renderPagination(grid, page, totalPages) {
      const existing = this.container.querySelector('.js-discount-pagination');
      if (existing) existing.remove();
      if (totalPages <= 1) return;

      const nav = document.createElement('nav');
      nav.className = 'pagination svg-color-inherit js-discount-pagination';
      nav.setAttribute('aria-label', 'Paginação');

      const ul = document.createElement('ul');
      ul.className = 'list-unstyled';

      this.paginationParts(page, totalPages).forEach(part => {
        const li = document.createElement('li');
        if (part === 'gap') {
          li.innerHTML = '<span>…</span>';
        } else if (part === page) {
          const span = document.createElement('span');
          span.className = 'current';
          span.setAttribute('aria-current', 'page');
          span.textContent = String(part);
          li.appendChild(span);
        } else {
          const a = document.createElement('a');
          a.href = this.pageUrl(part);
          a.dataset.page = String(part);
          a.setAttribute('role', 'button');
          a.textContent = String(part);
          li.appendChild(a);
        }
        ul.appendChild(li);
      });

      nav.appendChild(ul);
      grid.insertAdjacentElement('afterend', nav);
    }

    /** Janela de páginas: 1 … (p-1) p (p+1) … last. */
    paginationParts(page, totalPages) {
      const parts = [];
      for (let n = 1; n <= totalPages; n += 1) {
        if (n === 1 || n === totalPages || (n >= page - 1 && n <= page + 1)) {
          parts.push(n);
        } else if (parts[parts.length - 1] !== 'gap') {
          parts.push('gap');
        }
      }
      return parts;
    }

    pageUrl(page) {
      const p = new URLSearchParams(window.location.search);
      p.set('page', String(page));
      return `${window.location.pathname}?${p.toString()}`;
    }

    // Barra do topo (até o tablet) e título da sidebar (desktop).
    updateCount(total) {
      const text = total === 1 ? '1 produto' : `${total} produtos`;
      ['ProductCount', 'ProductCountAside'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.textContent = text;
      });
    }

    setLoading(on) {
      if (this.container) this.container.classList.toggle('discount-loading', on);
    }

    release() {
      this.owning = false;
      this.teardownLoadMore();
      const nav = this.container.querySelector('.js-discount-pagination');
      if (nav) nav.remove();
    }

    onContainerClick(event) {
      if (!this.owning) return;
      const link = event.target.closest('.js-discount-pagination a[data-page]');
      if (!link || !this.container.contains(link)) return;

      event.preventDefault();
      const page = parseInt(link.dataset.page, 10) || 1;
      history.pushState({ discountPage: page }, '', link.href);
      this.renderClientPage(page);
      this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Marca "Maior desconto" como selecionado. Só nos ATRIBUTOS: setar
    // select.value (IDL) sujaria o <select> e o custom-select do tema, que
    // troca a seleção por atributo, não conseguiria mais mudar de sort.
    reflectSelect() {
      document.querySelectorAll('select[name="sort_by"]').forEach(select => {
        const option = select.querySelector(`option[value="${SORT_VALUE}"]`);
        if (!option) return;

        select.querySelectorAll('option[selected]').forEach(o => o.removeAttribute('selected'));
        option.setAttribute('selected', 'selected');

        const customSelect = select.closest('custom-select');
        if (!customSelect) return;
        const button = customSelect.querySelector('.js-btn-dropdown');
        const target = customSelect.querySelector(`.js-btn-option[data-value="${SORT_VALUE}"]`);
        if (!button || !target) return;

        customSelect.querySelectorAll('.js-btn-option.is-selected').forEach(b => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-selected', 'false');
        });
        target.classList.add('is-selected');
        target.setAttribute('aria-selected', 'true');
        button.textContent = target.textContent;
        customSelect.dataset.value = SORT_VALUE;
      });

      // "Ordenar por" da sidebar (desktop): radios. Marcar um desmarca os
      // demais do grupo, e o bloco é recriado pelo facets.js a cada render.
      document
        .querySelectorAll(`input[type="radio"][name="sort_by"][value="${SORT_VALUE}"]`)
        .forEach(radio => {
          radio.checked = true;
        });
    }
  }

  let instance = null;
  function boot() {
    if (!instance) instance = new DiscountSort();
    instance.init();
  }

  if (document.readyState === 'complete') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  }
  document.addEventListener('shopify:section:load', boot);
})();
